// Tab schemas for the IM Transaction Entry form (Delphi uIMD0019). Consumed by the tab components via
// the dynamic FormField builder. Coded fields render as descriptive dropdowns (Architecture rule 3);
// options are injected in PrimaryTab.onFormLoad.
const f = (name, label, type, visible, extra) => ({
    name, tableName: "IMTRANSACTIONHEADER", label, type,
    required: false, visible, isLabelChanged: false, ...(extra || {}),
});

// User-defined passthrough columns (Code/Reference/Date/Quantity 1..10) — shown on the User Defined tab.
const codes = () => Array.from({ length: 10 }, (_, i) => f(`CODE${i + 1}`, `Code ${i + 1}`, "text", true));
const references = () => Array.from({ length: 10 }, (_, i) => f(`REFERENCE${i + 1}`, `Reference ${i + 1}`, "text", true));
const dates = () => Array.from({ length: 10 }, (_, i) => f(`DATE${i + 1}`, `Date ${i + 1}`, "date", true));
const quantities = () => Array.from({ length: 10 }, (_, i) => f(`QUANTITY${i + 1}`, `Quantity ${i + 1}`, "number", true));

export const PrimaryLayout = [
    // Left column — the core header fields (Delphi left group).
    {
        groupId: 1,
        label: "",
        fields: [
            f("RECORD", "Record #", "text", true, { localizedField: "KEYSEQUENCE", readOnly: true }),
            f("RPROFILE", "Profile", "select", true, { required: true }),
            f("TRANSACTIONTYPE", "Trans Type", "select", true),
            f("REFERENCE", "Reference #", "text", true),
            f("TRANSACTIONDATE", "Transaction", "date", true),
            f("DESCRIPTION", "Description", "text", true),
            f("STAGE", "Stage", "select", true),
        ],
    },
    // Status + fiscal (Delphi middle column, above the box). Comment / Import File live on the Additional tab.
    {
        groupId: 2,
        label: "",
        fields: [
            f("STATUS", "Status", "select", true),
            f("FISCALYEAR", "Fiscal Year", "number", true),
            f("FISCALPERIOD", "Fiscal Period", "number", true),
        ],
    },
    // Default Management Units (Delphi middle-column box) — optional GL dimension defaults.
    {
        groupId: 3,
        label: "Default Management Units",
        fields: [
            f("RDEFAULTDEPARTMENT", "Department", "select", true),
            f("RDEFAULTFUND", "Fund", "select", true),
            f("RDEFAULTJOB", "Job", "select", true),
        ],
    },
    // Totals (Delphi right column) — read-only rollups from the detail lines.
    {
        groupId: 4,
        label: "Totals",
        fields: [
            f("TOTALLINES", "Total Lines", "text", true, { readOnly: true }),
            f("TOTALQUANTITY", "Total Quantity", "text", true, { readOnly: true }),
            f("TOTALAMOUNT", "Total Amount", "text", true, { readOnly: true }),
        ],
    },
];

// Additional tab (Delphi uIMD0019 "Additional") — Comment + the read-only imported file name.
export const AdditionalLayout = [
    {
        groupId: 1,
        label: "",
        fields: [
            f("COMMENT", "Comment", "textarea", true),
            f("IMPORTFILENAME", "Import File Name", "text", true, { readOnly: true }),
        ],
    },
];

// User Defined tab (Delphi uIMD0019 "User Defined") — the 4x10 user-defined passthroughs grouped by kind
// (Codes / References / Dates / Quantities), plus the user-defined comment.
export const UserDefinedLayout = [
    { groupId: 1, label: "Codes", fields: codes() },
    { groupId: 2, label: "References", fields: references() },
    { groupId: 3, label: "Dates", fields: dates() },
    { groupId: 4, label: "Quantities", fields: quantities() },
    { groupId: 5, label: "", fields: [f("USERDEFINEDCOMMENT", "Comment", "textarea", true)] },
];

// Details tab -> "Primary" sub-tab schema (Delphi uIMD0019 Details/Primary). Bound to the SELECTED detail
// line (IMTransactionDetailDto, camelCase keys), read-only display for now. Field names match the grid
// row object keys so the shared selected-row object populates the form directly.
const df = (name, label, type, extra) => ({
    name, tableName: "IMTRANSACTIONDETAIL", label, type,
    required: false, visible: true, isLabelChanged: false, readOnly: true, ...(extra || {}),
});

export const DetailPrimaryLayout = [
    {
        groupId: 1,
        label: "Item",
        fields: [
            df("itemDescription", "Item", "text"),
            df("itemUom", "UOM", "text"),
            df("itemNetAvailable", "Net Available", "text"),
        ],
    },
    {
        groupId: 2,
        label: "Lot",
        fields: [
            df("lotDescription", "Lot", "text"),
            df("lotUom", "UOM", "text"),
            df("lotNetAvailable", "Net Available", "text"),
        ],
    },
];

// Editable detail field (not read-only), bound to the selected line by camelCase key.
const ef = (name, label, type) => ({
    name, tableName: "IMTRANSACTIONDETAIL", label, type,
    required: false, visible: true, isLabelChanged: false,
});

// Details -> "User Defined" sub-tab (Delphi uIMD0019 Details/User Defined) — the line's user-defined
// Code/Reference/Date/Quantity 1..5 passthroughs (detail carries 5 of each, vs 10 on the header).
const range5 = [1, 2, 3, 4, 5];
export const DetailUserDefinedLayout = [
    { groupId: 1, label: "Codes", fields: range5.map((i) => ef(`code${i}`, `Code ${i}`, "text")) },
    { groupId: 2, label: "References", fields: range5.map((i) => ef(`reference${i}`, `Reference ${i}`, "text")) },
    { groupId: 3, label: "Dates", fields: range5.map((i) => ef(`date${i}`, `Date ${i}`, "date")) },
    { groupId: 4, label: "Quantities", fields: range5.map((i) => ef(`quantity${i}`, `Quantity ${i}`, "number")) },
];

// Details -> "Miscellaneous" sub-tab (Delphi uIMD0019 "EF Interface") — meter / participant / fuel fields.
export const DetailMiscLayout = [
    {
        groupId: 1,
        label: "EF Interface",
        fields: [
            ef("meterId", "Meter ID", "select"),
            ef("participantId", "Participant", "select"),
            ef("meterReading", "Meter Reading", "number"),
        ],
    },
    {
        groupId: 2,
        label: "",
        fields: [
            ef("fuelReference", "Fuel Ref.", "text"),
            ef("fuelCity", "City", "text"),
            ef("fuelTaxId", "State", "select"),
        ],
    },
];

// Details -> "Comments" sub-tab (Delphi uIMD0019 Details/Comments) — the line's comment + user comment.
export const DetailCommentsLayout = [
    { groupId: 1, label: "", fields: [ef("comment", "Comment", "textarea")] },
    { groupId: 2, label: "", fields: [ef("userDefinedComment", "User Comment", "textarea")] },
];

// Details -> "Accounting" sub-tab (Delphi uIMD0019 Details/Accounting) — GL dimensions + vendor/asset/date
// for the selected line. Coded fields are server-loaded dropdowns (options injected in AccountingTab).
export const DetailAccountingLayout = [
    {
        groupId: 1,
        label: "",
        fields: [
            ef("accountId", "Account", "select"),
            ef("glDepartmentId", "Department", "select"),
            ef("fundId", "Fund", "select"),
            ef("jobId", "Job", "select"),
            ef("activityId", "Activity", "select"),
        ],
    },
    {
        groupId: 2,
        label: "",
        fields: [
            ef("transactionDate", "Date", "date"),
            ef("equipmentId", "Asset ID", "select"),
            ef("vendorId", "Vendor", "select"),
        ],
    },
];
